import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { 
  User, 
  Mail, 
  Smartphone, 
  Globe, 
  Clock, 
  Shield, 
  RefreshCcw, 
  Copy, 
  LogOut,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  ArrowLeft
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { mockProfile, mockTokens, mockDeviceInfo } from '../mock/mockData';
import { useNavigate } from 'react-router-dom';

const DemoProfilePage = () => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({});
  
  const profile = mockProfile;
  const tokens = mockTokens;
  const deviceInfo = mockDeviceInfo;

  const handleCopyJSON = (data, title) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast({
      title: "Copied to clipboard",
      description: `${title} data copied successfully.`,
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 p-4">
      <div className="max-w-md mx-auto space-y-4">
        {/* Demo Header */}
        <Card className="shadow-xl border-0 bg-blue-50 border-blue-200">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Smartphone className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-bold text-blue-800">Demo Mode</h2>
            </div>
            <p className="text-sm text-blue-600 mb-3">
              This shows how the profile page would look with real LINE data
            </p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/')}
              className="text-blue-600 border-blue-300 hover:bg-blue-100"
            >
              <ArrowLeft className="w-3 h-3 mr-1" />
              Back to Login
            </Button>
          </CardContent>
        </Card>

        {/* Profile Header */}
        <Card className="shadow-xl border-0">
          <CardContent className="p-6 text-center">
            <Avatar className="w-24 h-24 mx-auto mb-4 border-4 border-emerald-100">
              <AvatarImage src={profile?.pictureUrl} alt={profile?.displayName} />
              <AvatarFallback className="bg-emerald-500 text-white text-xl">
                {profile?.displayName?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <h1 className="text-xl font-bold text-slate-800 mb-1">
              {profile?.displayName || 'Unknown User'}
            </h1>
            <p className="text-sm text-slate-500 mb-4">
              {profile?.statusMessage || 'No status message'}
            </p>
            <div className="flex justify-center gap-2">
              <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                <User className="w-3 h-3 mr-1" />
                {profile?.userId?.substring(0, 8)}...
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Basic Info */}
        <Card className="shadow-xl border-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-500" />
              Profile Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">User ID</span>
              <span className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">
                {profile?.userId?.substring(0, 12)}...
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Display Name</span>
              <span className="text-sm font-medium">{profile?.displayName}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Profile Picture</span>
              <Badge variant={profile?.pictureUrl ? "default" : "secondary"}>
                {profile?.pictureUrl ? <CheckCircle className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
                {profile?.pictureUrl ? 'Available' : 'Not set'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Token Claims */}
        {tokens?.decodedToken && (
          <Card className="shadow-xl border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-500" />
                Token Claims
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {tokens.decodedToken.email && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Email</span>
                  <span className="text-sm font-medium">{tokens.decodedToken.email}</span>
                </div>
              )}
              {tokens.decodedToken.name && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Name</span>
                  <span className="text-sm font-medium">{tokens.decodedToken.name}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Subject</span>
                <span className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">
                  {tokens.decodedToken.sub?.substring(0, 12)}...
                </span>
              </div>
              {tokens.decodedToken.exp && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Expires</span>
                  <span className="text-sm font-medium">{formatDate(tokens.decodedToken.exp)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Device Info */}
        {deviceInfo && (
          <Card className="shadow-xl border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-emerald-500" />
                Device Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Platform</span>
                <span className="text-sm font-medium">{deviceInfo.os}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Language</span>
                <span className="text-sm font-medium">{deviceInfo.language}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">LIFF Version</span>
                <span className="text-sm font-medium">{deviceInfo.version}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">In LINE App</span>
                <Badge variant={deviceInfo.isInClient ? "default" : "secondary"}>
                  {deviceInfo.isInClient ? 'Yes' : 'No'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Expandable JSON Sections */}
        <div className="space-y-2">
          {/* Decoded ID Token */}
          <Collapsible 
            open={expandedSections.idToken} 
            onOpenChange={() => toggleSection('idToken')}
          >
            <Card className="shadow-lg border-0">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-emerald-500" />
                      ID Token Data
                    </span>
                    {expandedSections.idToken ? 
                      <ChevronDown className="w-4 h-4" /> : 
                      <ChevronRight className="w-4 h-4" />
                    }
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <pre className="text-xs font-mono text-slate-600 whitespace-pre-wrap break-words">
                      {JSON.stringify(tokens?.decodedToken, null, 2)}
                    </pre>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3 w-full" 
                    onClick={() => handleCopyJSON(tokens?.decodedToken, 'ID Token')}
                  >
                    <Copy className="w-3 h-3 mr-2" />
                    Copy JSON
                  </Button>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Profile Data */}
          <Collapsible 
            open={expandedSections.profile} 
            onOpenChange={() => toggleSection('profile')}
          >
            <Card className="shadow-lg border-0">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <User className="w-4 h-4 text-emerald-500" />
                      Profile Data
                    </span>
                    {expandedSections.profile ? 
                      <ChevronDown className="w-4 h-4" /> : 
                      <ChevronRight className="w-4 h-4" />
                    }
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <pre className="text-xs font-mono text-slate-600 whitespace-pre-wrap break-words">
                      {JSON.stringify(profile, null, 2)}
                    </pre>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3 w-full" 
                    onClick={() => handleCopyJSON(profile, 'Profile')}
                  >
                    <Copy className="w-3 h-3 mr-2" />
                    Copy JSON
                  </Button>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Device Info */}
          <Collapsible 
            open={expandedSections.device} 
            onOpenChange={() => toggleSection('device')}
          >
            <Card className="shadow-lg border-0">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-emerald-500" />
                      Device Data
                    </span>
                    {expandedSections.device ? 
                      <ChevronDown className="w-4 h-4" /> : 
                      <ChevronRight className="w-4 h-4" />
                    }
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <pre className="text-xs font-mono text-slate-600 whitespace-pre-wrap break-words">
                      {JSON.stringify(deviceInfo, null, 2)}
                    </pre>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3 w-full" 
                    onClick={() => handleCopyJSON(deviceInfo, 'Device Info')}
                  >
                    <Copy className="w-3 h-3 mr-2" />
                    Copy JSON
                  </Button>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-3 gap-2 pt-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => toast({ title: "Demo Mode", description: "Refresh functionality would work in real LIFF environment" })}
            className="flex items-center gap-1"
          >
            <RefreshCcw className="w-3 h-3" />
            Refresh
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleCopyJSON({ profile, tokens, deviceInfo }, 'All Data')}
            className="flex items-center gap-1"
          >
            <Copy className="w-3 h-3" />
            Copy All
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <LogOut className="w-3 h-3" />
            Logout
          </Button>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-slate-400 py-4 space-y-1">
          <p>Powered by LINE LIFF Platform</p>
          <p>Demo Mode - Data updated: {new Date().toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
};

export default DemoProfilePage;